import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Building2,
  FileText,
  ClipboardCheck,
  Factory,
  ShieldCheck,
  Ship,
  Radar,
  PackageCheck,
} from "lucide-react";
import { Reveal, Overline } from "./primitives";

const steps = [
  { icon: Building2, title: "Select Manufacturer", desc: "Choose a verified Turkish manufacturer for your full container order." },
  { icon: FileText, title: "Request Factory Quotation", desc: "Send a factory-direct quotation request straight from the Workspace." },
  { icon: ClipboardCheck, title: "Confirm Purchase Order", desc: "Review and sign one purchase order that governs the entire operation." },
  { icon: Factory, title: "Production", desc: "Track manufacturing progress with live production status updates." },
  { icon: ShieldCheck, title: "Inspection", desc: "Schedule and monitor quality inspection before your goods ship." },
  { icon: Ship, title: "Freight Booking", desc: "Book ocean freight with FreightIQ™ — connected end-to-end." },
  { icon: Radar, title: "Shipment Tracking", desc: "Follow every milestone with Live Shipment Tracking™." },
  { icon: PackageCheck, title: "Delivered", desc: "Arrival confirmed with a complete, centralized operation record." },
];

export const WorkflowSection = () => {
  const reduce = useReducedMotion();
  return (
    <section
      id="workflow"
      data-testid="workflow-section"
      className="border-t border-slate-100 bg-white py-24 lg:py-32"
    >
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Overline>How FullContainer™ Works</Overline>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink md:text-4xl">
            One connected workflow, from factory to delivery.
          </h2>
          <p className="mt-4 text-base text-slateink md:text-lg">
            Every stage flows into the next inside a single Workspace — no handoffs, no blind spots.
          </p>
        </Reveal>

        <div className="relative mt-16 pl-2">
          {/* animated vertical rail */}
          <div className="absolute left-[27px] top-2 bottom-2 w-px bg-slate-200" aria-hidden />
          <motion.div
            aria-hidden
            className="absolute left-[27px] top-2 w-px origin-top bg-gradient-to-b from-demax to-demax/20"
            initial={reduce ? false : { scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1.6, ease: "easeInOut" }}
            style={{ bottom: "0.5rem" }}
          />

          <div className="space-y-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                className="relative flex items-start gap-5"
                initial={reduce ? false : { opacity: 0, x: 12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-demax shadow-card-base">
                  <s.icon className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-5 shadow-card-base transition-all duration-300 hover:border-demax/30 hover:shadow-card-hover">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold tracking-wider text-slate-300">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-base font-semibold text-ink">{s.title}</h3>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-slateink">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
